import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import logo from '@/assets/logo.png';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success('Signed out successfully');
  };

  const isActive = (path: string) => location.pathname === path;

  const getNavClass = (path: string) =>
    isActive(path)
      ? 'text-foreground font-medium'
      : 'text-muted-foreground hover:text-foreground transition-colors';

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to={user ? "/home" : "/"} className="flex items-center">
            <img src={logo} alt="HiStage" className="h-14 w-auto brightness-110" />
          </Link>
          
          <nav className="hidden lg:flex items-center gap-6">
            <Link to="/about" className={getNavClass('/about')}>
              About
            </Link>
            <Link to="/blog" className={getNavClass('/blog')}>
              Blog
            </Link>
            <Link to="/careers" className={getNavClass('/careers')}>
              Careers
            </Link>
            <Link to="/help" className={getNavClass('/help')}>
              Help
            </Link>
            <Link to="/contact" className={getNavClass('/contact')}>
              Contact
            </Link>
          </nav>
          
          {user && (
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/home" className={getNavClass('/home')}>
                HOME
              </Link>
              <Link to="/discovery" className={getNavClass('/discovery')}>
                DISCOVER
              </Link>
              <Link to="/search" className={getNavClass('/search')}>
                SEARCH
              </Link>
              <Link to="/messages" className={getNavClass('/messages')}>
                MESSAGES
              </Link>
              {userRole === 'creator' && (
                <>
                  <Link to="/go-live" className={getNavClass('/go-live')}>
                    GO LIVE
                  </Link>
                  <Link to="/creator" className={getNavClass('/creator')}>
                    DASHBOARD
                  </Link>
                </>
              )}
              {userRole === 'admin' && (
                <>
                  <Link to="/analytics" className={getNavClass('/analytics')}>
                    ANALYTICS
                  </Link>
                  <Link to="/admin" className={getNavClass('/admin')}>
                    ADMIN
                  </Link>
                </>
              )}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-4">
          {user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/home')}>
                    Home
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/discovery')}>
                    Discover
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/search')}>
                    Search Users
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/messages')}>
                    Messages
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/wallet')}>
                    Wallet
                  </DropdownMenuItem>
                  {userRole === 'creator' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/go-live')}>
                        Go Live
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/creator')}>
                        Dashboard
                      </DropdownMenuItem>
                    </>
                  )}
                  {userRole === 'admin' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/analytics')}>
                        Analytics
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={handleSignOut}>
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </>
          ) : (
            <Button asChild variant="default">
              <Link to="/auth">Sign In</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
